import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getCallsByProjectId, ProjectCall } from "../../../services/grantsApi";
import { formatDateRange, formatAllocation } from "../../../utils/date-formatters";
import { CALL_STATUS_LABELS, CALL_STATUS_COLORS } from "../../../constants/grants-labels";

/**
 * Props for ProjectCallsSection component
 * @see docs/specifications/2026-01-30-recommendation-details-project-context.md
 */
interface ProjectCallsSectionProps {
  projectId: string;
  targetCallId?: string | null;
}

/**
 * Skeleton loader for calls list
 */
const CallsSkeleton: React.FC = () => (
  <div className="space-y-3" aria-label="Ładowanie naborów...">
    {[1, 2, 3].map((i) => (
      <div key={i} className="animate-pulse flex items-center gap-4 p-3 border border-neutral-200 rounded-md">
        <div className="flex-1 space-y-2">
          <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
          <div className="h-3 bg-neutral-200 rounded w-1/2"></div>
        </div>
        <div className="h-6 bg-neutral-200 rounded w-20"></div>
      </div>
    ))}
  </div>
);

/**
 * Error alert with retry button
 */
const ErrorAlert: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="p-4 bg-red-50 border border-red-200 rounded-md">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span className="text-sm text-red-700">{message}</span>
      </div>
      <button
        onClick={onRetry}
        className="px-3 py-1 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50"
      >
        Odśwież
      </button>
    </div>
  </div>
);

/**
 * Empty state message
 */
const EmptyState: React.FC = () => (
  <p className="text-center text-sm text-neutral-500 py-4">
    Brak naborów w tym projekcie
  </p>
);

/**
 * Single call row in the list
 */
const CallRow: React.FC<{ call: ProjectCall; isTarget: boolean }> = ({ call, isTarget }) => {
  const statusLabel = CALL_STATUS_LABELS[call.status] || call.status;
  const statusColor = CALL_STATUS_COLORS[call.status] || "bg-neutral-200 text-neutral-800";

  return (
    <Link
      to={`/grants/calls/${call.id}`}
      className={`block p-3 rounded-md border transition-colors focus-visible:ring-2 focus-visible:ring-primary-500 ${
        isTarget
          ? "bg-primary-50 border-l-4 border-l-primary-500 border-primary-200 hover:bg-primary-100"
          : "border-neutral-200 hover:bg-neutral-50"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-neutral-900 truncate">{call.title}</span>
            {isTarget && (
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-700 text-white">
                Docelowy
              </span>
            )}
          </div>
          <div className="mt-1 flex items-center gap-4 text-xs text-neutral-500">
            {call.callNumber && (
              <span>Nr: {call.callNumber}</span>
            )}
            <span>{formatDateRange(call.applicationStartDate, call.applicationEndDate)}</span>
            {call.allocation !== null && (
              <span>{formatAllocation(call.allocation)}</span>
            )}
          </div>
        </div>
        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${statusColor}`}>
          {statusLabel}
        </span>
      </div>
    </Link>
  );
};

/**
 * Section displaying existing calls in a project.
 * Used in recommendation details page to show context.
 *
 * @see docs/specifications/2026-01-30-recommendation-details-project-context.md
 */
const ProjectCallsSection: React.FC<ProjectCallsSectionProps> = ({ projectId, targetCallId }) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["project-calls", projectId],
    queryFn: () => getCallsByProjectId(projectId, 50),
    enabled: !!projectId,
    staleTime: 60000, // 1 minute
  });

  const calls = data?.data || [];
  const totalCount = data?.pagination?.totalCount || calls.length;

  // Error message mapping
  const getErrorMessage = (err: unknown): string => {
    if (err instanceof Error) {
      if (err.name === "TimeoutError" || err.message.includes("timeout")) {
        return "Przekroczono czas oczekiwania";
      }
      if (err.message.includes("network") || err.message.includes("Network")) {
        return "Błąd połączenia";
      }
    }
    return "Nie udało się pobrać naborów";
  };

  return (
    <section aria-labelledby="project-calls-heading" className="bg-white rounded-lg shadow-sm border border-neutral-200">
      <div className="px-6 py-4 border-b border-neutral-200">
        <h2 id="project-calls-heading" className="text-lg font-medium text-neutral-900 text-left">
          Istniejące nabory ({isLoading ? "..." : totalCount})
        </h2>
      </div>
      <div className="px-6 py-4">
        {isLoading && <CallsSkeleton />}

        {error && (
          <ErrorAlert message={getErrorMessage(error)} onRetry={() => refetch()} />
        )}

        {!isLoading && !error && calls.length === 0 && <EmptyState />}

        {!isLoading && !error && calls.length > 0 && (
          <>
            <div className="space-y-2">
              {calls.map((call) => (
                <CallRow key={call.id} call={call} isTarget={call.id === targetCallId} />
              ))}
            </div>
            {totalCount > 50 && (
              <p className="mt-3 text-xs text-neutral-500 text-center">
                Pokazano 50 najnowszych naborów z {totalCount}
              </p>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default ProjectCallsSection;
